import OpcaoSelecao from '@/components/base/OpcaoSelecao';
import type { Participacao } from '@/types/evento-publico';
import { cidadesDosGrupos, dividirNomeGrupo, ehGrupoEmDuasEtapas } from '@/lib/grupoOpcao';
import { textoDoValor, vagasRestantes } from '@/pages/evento/utils';

interface CartaoGrupoProps {
  grupos: Participacao[];
  selecionado: number | null;
  onChange: (opcaoId: number | null) => void;
  cidade: string | null;
  onEscolherCidade: (cidade: string) => void;
  destaque?: 'grupo' | 'cidade' | 'sexo' | null;
}

export default function CartaoGrupo({
  grupos,
  selecionado,
  onChange,
  cidade,
  onEscolherCidade,
  destaque,
}: CartaoGrupoProps) {
  const duasEtapas = ehGrupoEmDuasEtapas(grupos);
  const cidades = duasEtapas ? cidadesDosGrupos(grupos) : [];

  // Borda vermelha por alguns segundos no bloco que faltou escolher.
  const marca = (alvo: 'grupo' | 'cidade' | 'sexo') =>
    destaque === alvo ? 'rounded-lg ring-2 ring-danger-500' : '';

  if (!duasEtapas) {
    return (
      <div id="bloco-grupo" className={marca('grupo')}>
        <div className="rounded-lg border border-secondary-200 bg-white p-6">
          <h2 className="text-base font-medium text-foreground-950">
            Escolha uma opção <span className="text-danger-600">*</span>
          </h2>
          <p className="mt-1 text-sm text-secondary-500">
            Cada opção tem sua própria quantidade de vagas.
          </p>

          <div className="mt-3 space-y-1">
            {grupos.map((g) => {
              const esgotado = g.situacao === 'esgotado';
              const vagas = vagasRestantes(g.restantes);
              return (
                <OpcaoSelecao
                  key={g.opcao_id ?? g.modalidade_id}
                  tipo="radio"
                  checked={selecionado === g.opcao_id}
                  onChange={() => g.opcao_id != null && onChange(g.opcao_id)}
                  disabled={esgotado}
                >
                  <span className="flex flex-col">
                    <span className="font-medium">{g.grupo}</span>
                    <span className="text-sm text-secondary-500">
                      {textoDoValor(g)}
                      {esgotado ? ' · Esgotado' : ''}
                    </span>
                    {vagas && !esgotado && (
                      <span className="text-xs font-medium text-amber-600">{vagas}</span>
                    )}
                  </span>
                </OpcaoSelecao>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const opcoesDaCidade = cidade
    ? grupos.filter((g) => dividirNomeGrupo(g.grupo)?.cidade === cidade)
    : [];

  function escolherCidade(nova: string) {
    const mudou = nova !== cidade;
    onEscolherCidade(nova);
    // O grupo marcado era de outra cidade? Limpa a escolha.
    const g = grupos.find((p) => p.opcao_id === selecionado);
    const cidadeDoGrupo = dividirNomeGrupo(g?.grupo ?? null)?.cidade ?? null;
    if (cidadeDoGrupo && cidadeDoGrupo !== nova) onChange(null);

    // Depois de escolher a cidade, leva a pessoa até "Masculino ou Feminino?",
    // para ela ver que ainda tem uma escolha abaixo.
    if (mudou) {
      window.setTimeout(() => {
        document
          .getElementById('bloco-sexo')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 140);
    }
  }

  return (
    <div id="bloco-grupo" className="space-y-4">
      <div id="bloco-cidade" className={marca('cidade')}>
        <div className="rounded-lg border border-secondary-200 bg-white p-6">
          <h2 className="text-base font-medium text-foreground-950">
            De onde você está fazendo a ficha? <span className="text-danger-600">*</span>
          </h2>
          <p className="mt-1 text-sm text-secondary-500">Escolha a cidade da sua ficha.</p>

          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {cidades.map((c) => {
              const ativa = cidade === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => escolherCidade(c)}
                  className={`flex cursor-pointer items-center justify-between gap-2 whitespace-nowrap rounded-md border px-4 py-3 text-left text-[15px] font-medium transition-colors ${
                    ativa
                      ? 'border-primary-500 bg-primary-50 text-foreground-950'
                      : 'border-secondary-300 bg-white text-foreground-800 hover:bg-background-50'
                  }`}
                >
                  {c}
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                      ativa
                        ? 'border-primary-500 bg-primary-500 text-white'
                        : 'border-secondary-300 text-transparent'
                    }`}
                  >
                    <i className="ri-check-line text-xs" aria-hidden />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {cidade && (
        <div id="bloco-sexo" className={marca('sexo')}>
          <div className="rounded-lg border border-secondary-200 bg-white p-6">
            <h2 className="text-base font-medium text-foreground-950">
              Masculino ou Feminino? <span className="text-danger-600">*</span>
            </h2>
            <p className="mt-1 text-sm text-secondary-500">{cidade}</p>

            <div className="mt-3 space-y-1">
              {opcoesDaCidade.map((g) => {
                const esgotado = g.situacao === 'esgotado';
                const vagas = vagasRestantes(g.restantes);
                const sexo = dividirNomeGrupo(g.grupo)?.sexo ?? g.grupo;
                return (
                  <OpcaoSelecao
                    key={g.opcao_id ?? g.modalidade_id}
                    tipo="radio"
                    checked={selecionado === g.opcao_id}
                    onChange={() => g.opcao_id != null && onChange(g.opcao_id)}
                    disabled={esgotado}
                  >
                    <span className="flex flex-col">
                      <span className="font-medium">{sexo}</span>
                      <span className="text-sm text-secondary-500">
                        {textoDoValor(g)}
                        {esgotado ? ' · Esgotado' : ''}
                      </span>
                      {vagas && !esgotado && (
                        <span className="text-xs font-medium text-amber-600">{vagas}</span>
                      )}
                    </span>
                  </OpcaoSelecao>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}